import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take, tap } from 'rxjs/operators';
import { SessionService } from './session.service';
import { Encryption } from './encryption.service';
import { LoginInfo, LoginService } from '../../API/request-services/login.service';
import { API, LS_LOGIN_INFO } from '../../const';
import { MessageService } from 'primeng/api';
import { Severity } from '../components/info-toast/models/severity';
import { L10nStatic } from '../static/l10n.static';
import { UIToast } from '../models/toast.models';
import ToastKey = UIToast.ToastKey;

// TODO: возможно может быть ошибка если рефреш по времени будет вызван сразу после обновления токена... надо тестить

// глобальное состояние. одно на всё приложение
let isRefreshing = false;
const refreshGate$ = new BehaviorSubject<boolean>(true); // true => можно слать запросы, false => идёт refresh

// вспомогательные функции интерсептора
const getAuthFromStorage = (): LoginInfo | null => Encryption.getLsValue<LoginInfo>(LS_LOGIN_INFO);
const getAuthHeader = (): string | null => {
	const li = getAuthFromStorage();
	return li ? `${li.token}:${li.key}` : null;
};

const shouldSkipAuth = (url: string): boolean => {
	return url.toLowerCase().includes(API.auth.refreshV2) || url.toLowerCase().includes(API.auth.loginV2);
};

const addAuth = (req: HttpRequest<any>): HttpRequest<any> => {
	if (req.headers.has('Authorization')) return req;
	const auth = getAuthHeader();
	return auth ? req.clone({ headers: req.headers.set('Authorization', auth) }) : req;
};

// пометка ретрая (чтобы не уйти в вечный цикл)
const markRetried = (req: HttpRequest<any>): HttpRequest<any> =>
	req.clone({ headers: req.headers.set('X-Retry', '1') });
const isRetried = (req: HttpRequest<any>) => req.headers.get('X-Retry') === '1';

// ожидание завершения рефреша перед отправкой запроса
const waitForGateThenSend = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> =>
	refreshGate$.pipe(
		filter((open) => open === true),
		take(1),
		switchMap(() => next(addAuth(req))),
	);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
	const injector = inject(Injector);

	// если идёт рефреш то удерживаем любые НЕ-auth запросы до открытия типа "шлюза"
	if (!shouldSkipAuth(req.url) && isRefreshing) {
		return waitForGateThenSend(req, next);
	}

	// обычный запуск (auth-запросы будут пропускаться без заголовка)
	const initial = shouldSkipAuth(req.url) ? req : addAuth(req);

	const handle401 = (failedReq: HttpRequest<any>, nextFn: HttpHandlerFn): Observable<HttpEvent<any>> => {
		// если это повтор после рефреша и снова 401 — не зацикливаемся
		if (isRetried(failedReq) || shouldSkipAuth(failedReq.url)) {
			return throwError(() => ({ status: 401 }));
		}

		// если refresh уже идёт то ждём и ретраим запросы
		if (isRefreshing) {
			return waitForGateThenSend(failedReq, nextFn).pipe(
				switchMap(() => nextFn(addAuth(markRetried(failedReq)))),
			);
		}

		// запускаем рефреша один раз
		isRefreshing = true;
		refreshGate$.next(false);

		const sessionService = injector.get(SessionService);
		const loginService = injector.get(LoginService);

		const current = getAuthFromStorage();
		const currentRefresh = current?.refreshToken;

		if (!currentRefresh) {
			// если нет refreshToken то смысла рефрешить нет
			isRefreshing = false;
			refreshGate$.next(true);
			return throwError(() => ({ status: 401 }));
		}

		// рефреш без Authorization. только с телом запроса { refreshToken }
		const refreshReqBody = { refreshToken: currentRefresh };

		return loginService.refreshToken(refreshReqBody).pipe(
			tap((loginInfo: LoginInfo) => {
				// сохранение нового токена и перезапуск проверки по сроку жизни
				sessionService.tokenService.saveTokenInfoToStorage(loginInfo, false);
				sessionService.tokenService.setupRegularTokenCheck();
			}),
			// открывание шлюза для новых запросов
			tap(() => refreshGate$.next(true)),
			// ретрай исходного запроса один раз
			switchMap(() => nextFn(addAuth(markRetried(failedReq)))),
			catchError((err) => {
				// если провал рефреша то разблокируем очередь и редирект на логин
				refreshGate$.next(true);
				injector.get(MessageService).add({
					severity: Severity.warn,
					detail: L10nStatic.translate('app.token_update_error_title'),
					summary: L10nStatic.translate('app.token_update_error_message'),
					key: ToastKey.appComponentBottomNotificator,
					closable: true,
					sticky: true,
				});
				sessionService.logout(true);
				return throwError(() => err);
			}),
			finalize(() => {
				isRefreshing = false;
			}),
		);
	};

	return next(initial).pipe(
		catchError((err) => {
			if (!shouldSkipAuth(req.url) && err?.status === 401) {
				return handle401(req, next);
			}
			return throwError(() => err);
		}),
	);
};
